import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useAccounts } from "./useAccounts";
import type { AnyValue } from "@/lib/rowTypes";

export type Screen =
  | 'unlock' | 'scenario_setup' | 'home_selection'
  | 'down_payment' | 'closing_costs' | 'mortgage_choice'
  | 'affordability_check' | 'final_summary' | 'life_shock'
  | 'delay_ending' | 'completed';

export interface FictionalHome {
  id: string;
  home_key: string;
  home_name: string;
  home_price: number;
  property_tax_monthly: number;
  insurance_monthly: number;
  maintenance_rating: string;
  description: string;
  display_order: number;
}

export interface FinancialProfile {
  checking: number;
  savings: number;
  emergencyFund: number;
  monthlyIncome: number;
  monthlyExpenses: number;
}

export interface ClosingCostsBreakdown {
  lenderFees: number;
  appraisalInspection: number;
  titleLegal: number;
  taxesPrepaids: number;
  insuranceSetup: number;
  total: number;
}

/* ============================================================ */
/* Decision-log + life-shock types. Local sim state, persisted   */
/* into homeownership_simulations.run_summary jsonb at run end.  */
/* ============================================================ */

export type HomeDecisionKey =
  | 'home_choice' | 'down_payment' | 'mortgage_term'
  | 'affordability' | 'life_shock' | 'final';

export interface HomeDecisionEntry {
  key: HomeDecisionKey;
  label: string;
  choice: string;
  consequence: string;
  lumiSignal: string;
  walletImpact: number;   // negative = cost, positive = saved
  scoreDelta: number;     // -10..+15 judgment score
}

export type LifeShockChoice = 'pay_cash' | 'use_credit' | 'postpone';

export interface LifeShockEvent {
  amount: number;
  description: string;
  choice: LifeShockChoice | null;
}

export type HomeReadinessLabel =
  | 'Equity Strategist'
  | 'Steady Owner'
  | 'Stretched Buyer'
  | 'House-Poor Risk'
  | 'Patient Planner';

/* Deterministic homeowner surprise sized to the home. Lower-priced */
/* homes still throw at least a $1,200 maintenance hit.             */
function buildLifeShock(home: FictionalHome | null): LifeShockEvent {
  if (!home) return { amount: 0, description: '', choice: null };
  const base = Math.round(home.home_price * 0.012);
  const amount = Math.max(1200, Math.min(8500, base));
  const candidates = [
    'Six months in: the HVAC system fails on the first cold snap and needs full replacement.',
    'A summer storm exposes a leaking roof — multiple shingles and underlayment must be replaced.',
    'The county sends a reassessment letter: property taxes jump for the rest of the year.',
    'The water heater bursts overnight and floods part of the basement.',
    'A tree falls in the yard and damages the fence and a corner of the garage.',
  ];
  const idx = (home.home_name.length + home.home_price) % candidates.length;
  return { amount, description: candidates[idx], choice: null };
}

export const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const DEFAULT_PROFILE: FinancialProfile = {
  checking: 18000, savings: 30000, emergencyFund: 12000,
  monthlyIncome: 5500, monthlyExpenses: 2300,
};

export function calcClosingCosts(price: number): ClosingCostsBreakdown {
  const total = Math.round(price * 0.0315);
  return {
    lenderFees: Math.round(total * 0.21),
    appraisalInspection: Math.round(total * 0.11),
    titleLegal: Math.round(total * 0.21),
    taxesPrepaids: Math.round(total * 0.37),
    insuranceSetup: Math.round(total * 0.10),
    total,
  };
}

export function calcMonthlyPI(loan: number, rate: number, years: number) {
  const r = rate / 12;
  const n = years * 12;
  if (r === 0) return Math.round(loan / n);
  return Math.round(loan * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1));
}

const SCREEN_PROGRESS: Record<Screen, number> = {
  unlock: 5, scenario_setup: 10, home_selection: 25,
  down_payment: 40, closing_costs: 55, mortgage_choice: 70,
  affordability_check: 80, final_summary: 90, life_shock: 96,
  delay_ending: 100, completed: 100,
};

export function useHomeBuyingSim() {
  const { user } = useAuth();
  const { accounts, refetch: refetchAccounts } = useAccounts();

  const [simId, setSimId] = useState<string | null>(null);
  const [screen, setScreenRaw] = useState<Screen>('unlock');
  const [homes, setHomes] = useState<FictionalHome[]>([]);
  const [profile, setProfile] = useState<FinancialProfile>(DEFAULT_PROFILE);
  const [scenarioType, setScenarioType] = useState<'live' | 'default'>('default');
  const [selectedHomeKey, setSelectedHomeKey] = useState<string | null>(null);
  const [downPaymentPct, setDownPaymentPct] = useState(0.1);
  const [mortgageYears, setMortgageYears] = useState(30);
  const [finalDecision, setFinalDecision] = useState<string | null>(null);
  const [purchaseCompleted, setPurchaseCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [popupKey, setPopupKey] = useState<string | null>(null);

  /* Local sim state, persisted into run_summary at completion. */
  const [decisionLog, setDecisionLog] = useState<HomeDecisionEntry[]>([]);
  const [committedSteps, setCommittedSteps] = useState<Record<string, boolean>>({});
  const [lifeShock, setLifeShock] = useState<LifeShockEvent>({ amount: 0, description: '', choice: null });
  const [lastDecision, setLastDecision] = useState<HomeDecisionEntry | null>(null);

  const ref = useRef({ selectedHomeKey, downPaymentPct, mortgageYears, profile, scenarioType, decisionLog, lifeShock });
  useEffect(() => { ref.current = { selectedHomeKey, downPaymentPct, mortgageYears, profile, scenarioType, decisionLog, lifeShock }; });

  // Load homes
  useEffect(() => {
    supabase.from("fictional_homes").select("*").order("display_order").then(({ data }) => {
      setHomes((data) || []);
    });
  }, []);

  // Load existing simulation
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("homeownership_simulations")
        .select("*").eq("user_id", user.id)
        .order("created_at", { ascending: false }).limit(1);
      if (data && (data).length > 0) {
        const s = (data)[0];
        setSimId(s.id);
        setScreenRaw(s.current_screen as Screen);
        setScenarioType(s.scenario_type === 'live_user_finances' ? 'live' : 'default');
        if (s.financial_profile && Object.keys(s.financial_profile).length > 0) setProfile(s.financial_profile as AnyValue);
        if (s.selected_home_key) setSelectedHomeKey(s.selected_home_key);
        if (s.down_payment_percent != null) setDownPaymentPct(Number(s.down_payment_percent));
        if (s.mortgage_term_years) setMortgageYears(s.mortgage_term_years);
        setFinalDecision(s.final_decision);
        setPurchaseCompleted(!!s.purchase_completed);
      if (s.run_summary && typeof s.run_summary === 'object') {
        const rs: AnyValue = s.run_summary;
        if (Array.isArray(rs.decision_log)) setDecisionLog(rs.decision_log);
        if (rs.life_shock) setLifeShock(rs.life_shock);
      }
      }
      setLoading(false);
    })();
  }, [user]);

  const selectedHome = homes.find(h => h.home_key === selectedHomeKey) || null;

  // Calculations
  const downPaymentAmount = selectedHome ? Math.round(selectedHome.home_price * downPaymentPct) : 0;
  const closingCosts = selectedHome ? calcClosingCosts(selectedHome.home_price) : { lenderFees: 0, appraisalInspection: 0, titleLegal: 0, taxesPrepaids: 0, insuranceSetup: 0, total: 0 };
  const loanAmount = selectedHome ? selectedHome.home_price - downPaymentAmount : 0;
  const rate = mortgageYears === 15 ? 0.059 : 0.065;
  const monthlyPI = loanAmount > 0 ? calcMonthlyPI(loanAmount, rate, mortgageYears) : 0;
  const totalMonthlyHousing = selectedHome ? monthlyPI + selectedHome.property_tax_monthly + selectedHome.insurance_monthly : 0;
  const cashToClose = downPaymentAmount + closingCosts.total;
  const liquidBefore = profile.checking + profile.savings;
  const liquidAfter = liquidBefore - cashToClose;
  const monthlyRemaining = profile.monthlyIncome - profile.monthlyExpenses - totalMonthlyHousing;
  const canAfford = liquidAfter >= 0;
  const affordabilityRating = selectedHome ? (() => {
    if (monthlyRemaining >= 1000 && liquidAfter >= 10000 && profile.emergencyFund > 0) return 'comfortable';
    if (monthlyRemaining >= 300 && liquidAfter >= 3000) return 'tight';
    return 'risky';
  })() : null;
  const progress = SCREEN_PROGRESS[screen] || 0;

  // Persist to DB
  const persist = useCallback(async (screenOverride: Screen, extra?: Record<string, AnyValue>) => {
    if (!user) return;
    const r2 = ref.current;
    const payload: AnyValue = {
      current_screen: screenOverride,
      selected_home_key: r2.selectedHomeKey,
      down_payment_percent: r2.downPaymentPct,
      mortgage_term_years: r2.mortgageYears,
      financial_profile: r2.profile,
      scenario_type: r2.scenarioType === 'live' ? 'live_user_finances' : 'default_profile',
      updated_at: new Date().toISOString(),
      run_summary: {
        decision_log: r2.decisionLog,
        life_shock: r2.lifeShock,
        ...(extra && (extra)._recap ? { recap: (extra)._recap } : {}),
      },
      ...extra,
    };
    delete (payload)._recap;
    if (simId) {
      await supabase.from("homeownership_simulations").update(payload).eq("id", simId);
    }
  }, [user, simId]);

  const goTo = useCallback((s: Screen) => {
    setScreenRaw(s);
    persist(s);
  }, [persist]);

  /* ----- Decision recorder ----- */
  const recordDecision = useCallback((entry: HomeDecisionEntry) => {
    setDecisionLog((log) => {
      const without = log.filter((e) => e.key !== entry.key);
      return [...without, entry];
    });
    setCommittedSteps((c) => ({ ...c, [entry.key]: true }));
    setLastDecision(entry);
  }, []);

  const commitHomeChoice = useCallback(() => {
    if (!selectedHome) return;
    const priceVsIncome = selectedHome.home_price / Math.max(1, profile.monthlyIncome * 12);
    const expensive = priceVsIncome > 5;
    const modest = priceVsIncome <= 3.5;
    recordDecision({
      key: 'home_choice',
      label: 'Home choice',
      choice: `${selectedHome.home_name} · ${fmt(selectedHome.home_price)}`,
      consequence: expensive
        ? `Price is ${priceVsIncome.toFixed(1)}× your annual income — most lenders consider this stretched.`
        : modest
          ? `Price stays at ~${priceVsIncome.toFixed(1)}× your annual income — leaves room for life.`
          : `Price is about ${priceVsIncome.toFixed(1)}× your annual income — within the common range.`,
      lumiSignal: expensive
        ? 'A house priced above 4–5× your income usually means a tight budget for everything else.'
        : modest
          ? 'A modestly priced home is the single biggest predictor of a low-stress mortgage.'
          : 'You picked in the typical first-buyer band — the rest depends on down payment and term.',
      walletImpact: 0,
      scoreDelta: expensive ? -6 : modest ? 8 : 3,
    });
  }, [selectedHome, profile.monthlyIncome, recordDecision]);

  const commitDownPayment = useCallback(() => {
    if (!selectedHome) return;
    const aggressive = downPaymentPct >= 0.2;
    const minimal = downPaymentPct <= 0.05;
    const cushionHit = profile.emergencyFund > 0
      ? Math.max(0, downPaymentAmount + closingCosts.total - (profile.checking + profile.savings - 5000))
      : 0;
    recordDecision({
      key: 'down_payment',
      label: 'Down payment',
      choice: `${Math.round(downPaymentPct * 100)}% down (${fmt(downPaymentAmount)})`,
      consequence: cushionHit > 0
        ? `Upfront cash pressures your cushion by ${fmt(cushionHit)}.`
        : aggressive
          ? '20%+ down avoids PMI and shrinks total interest.'
          : minimal
            ? 'Tiny down keeps savings intact but maximizes the loan (and likely adds PMI).'
            : 'Balanced down payment — moderate loan, moderate cushion.',
      lumiSignal: aggressive
        ? 'A bigger down payment is the cheapest way to lower lifetime mortgage cost — if it does not wipe out reserves.'
        : minimal
          ? 'A low down payment with no emergency cash is how new owners end up borrowing for the first repair.'
          : '10–20% is the usual sweet spot when you also need to fund closing costs and life.',
      walletImpact: -downPaymentAmount,
      scoreDelta: aggressive ? 8 : minimal ? -4 : 4,
    });
  }, [selectedHome, downPaymentPct, downPaymentAmount, closingCosts.total, profile, recordDecision]);

  const commitMortgageTerm = useCallback(() => {
    if (!selectedHome) return;
    const totalInterest = monthlyPI * 12 * mortgageYears - loanAmount;
    const long = mortgageYears >= 30;
    recordDecision({
      key: 'mortgage_term',
      label: 'Mortgage term',
      choice: `${mortgageYears}-year @ ${(rate * 100).toFixed(2)}%`,
      consequence: `Total interest over the loan ≈ ${fmt(Math.max(0, totalInterest))}.`,
      lumiSignal: long
        ? 'A 30-year loan lowers the monthly payment but quietly adds tens of thousands in interest.'
        : 'A 15-year loan costs more each month but pays the least interest and builds equity fastest.',
      walletImpact: -Math.max(0, totalInterest),
      scoreDelta: long ? -3 : 7,
    });
  }, [selectedHome, mortgageYears, monthlyPI, loanAmount, rate, recordDecision]);

  const commitAffordability = useCallback(() => {
    if (!affordabilityRating) return;
    const meta = {
      comfortable: { signal: 'You preserved a real monthly margin — the cushion that absorbs surprises.', score: 8 },
      tight: { signal: 'You can afford the payment, but a single bad month gets uncomfortable fast.', score: 0 },
      risky: { signal: 'Affordability is shaped by the worst month, not the average month. This one is fragile.', score: -7 },
    }[affordabilityRating] || { signal: '', score: 0 };
    recordDecision({
      key: 'affordability',
      label: 'Affordability decision',
      choice: `Proceeded with ${affordabilityRating} budget`,
      consequence: `Monthly remaining ${fmt(monthlyRemaining)} · Liquid after ${fmt(liquidAfter)}.`,
      lumiSignal: meta.signal,
      walletImpact: 0,
      scoreDelta: meta.score,
    });
  }, [affordabilityRating, monthlyRemaining, liquidAfter, recordDecision]);

  const ensureLifeShock = useCallback(() => {
    if (!lifeShock.amount && selectedHome) {
      setLifeShock(buildLifeShock(selectedHome));
    }
  }, [lifeShock.amount, selectedHome]);

  const commitLifeShock = useCallback((choice: LifeShockChoice) => {
    const ev = ref.current.lifeShock;
    if (!ev.amount) return;
    setLifeShock((cur) => ({ ...cur, choice }));
    const meta: Record<LifeShockChoice, { consequence: string; signal: string; impact: number; score: number; }> = {
      pay_cash: {
        consequence: `Paid ${fmt(ev.amount)} from savings. One-time hit, no follow-on cost.`,
        signal: 'Owning a home means budgeting for surprises. The emergency fund is the real homeowner insurance.',
        impact: -ev.amount,
        score: 6,
      },
      use_credit: {
        consequence: `Charged ${fmt(ev.amount)} to credit. Carried balance grows with interest until paid off.`,
        signal: 'Credit turns a one-time repair into a long, expensive tail. Use it only when cash truly is not available.',
        impact: -Math.round(ev.amount * 1.22),
        score: -5,
      },
      postpone: {
        consequence: `Repair postponed. Damage compounds; the eventual fix is larger and more disruptive.`,
        signal: 'Postponed home repairs almost always cost more later — and can become a safety issue.',
        impact: -Math.round(ev.amount * 1.5),
        score: -7,
      },
    };
    const m = meta[choice];
    recordDecision({
      key: 'life_shock',
      label: 'Surprise expense',
      choice: choice === 'pay_cash' ? 'Pay from savings'
        : choice === 'use_credit' ? 'Put on credit'
        : 'Postpone the repair',
      consequence: m.consequence,
      lumiSignal: m.signal,
      walletImpact: m.impact,
      scoreDelta: m.score,
    });
  }, [recordDecision]);

  const buildRecap = useCallback((didPurchase: boolean) => {
    const log = ref.current.decisionLog;
    const totalWallet = log.reduce((s, e) => s + e.walletImpact, 0);
    const totalScore = log.reduce((s, e) => s + e.scoreDelta, 0);
    const strongest = [...log].sort((a, b) => b.scoreDelta - a.scoreDelta)[0] || null;
    const weakest = [...log].sort((a, b) => a.scoreDelta - b.scoreDelta)[0] || null;

    let readiness: HomeReadinessLabel = 'Steady Owner';
    if (!didPurchase) readiness = 'Patient Planner';
    else if (totalScore >= 22) readiness = 'Equity Strategist';
    else if (totalScore >= 8) readiness = 'Steady Owner';
    else if (totalScore >= -4) readiness = 'Stretched Buyer';
    else readiness = 'House-Poor Risk';

    const tradeoff = (() => {
      const termE = log.find((e) => e.key === 'mortgage_term');
      const dpE = log.find((e) => e.key === 'down_payment');
      const shockE = log.find((e) => e.key === 'life_shock');
      if (shockE && shockE.scoreDelta < 0)
        return 'You handled the surprise expense in the most expensive way — homeowner math actually lives in the unexpected months.';
      if (termE && termE.scoreDelta < 0)
        return 'You chose a lower monthly payment but paid for it in long-term interest.';
      if (dpE && dpE.scoreDelta < 0)
        return 'A small down payment kept cash on hand but left you owing almost the full home price.';
      return 'You balanced down payment, monthly housing, and reserves — the three knobs every buyer adjusts.';
    })();

    const takeaway = didPurchase
      ? 'Affordability is not "Can I make the monthly?" — it is "Can I make the monthly AND still absorb a surprise repair?"'
      : 'Walking away is a real decision. Most expensive home mistakes are the ones bought because the buyer was already at the closing table.';

    const nextAction = didPurchase
      ? 'Open Risk Protection next: homeowner insurance and an emergency cushion decide whether one repair becomes a financial event.'
      : 'Try the Budget Builder module to see exactly how much room your real monthly cash flow has for housing.';

    return { totalWallet, totalScore, readiness, strongest, weakest, tradeoff, takeaway, nextAction };
  }, []);

  const startSim = useCallback(async () => {
    let newProfile = DEFAULT_PROFILE;
    let type: 'live' | 'default' = 'default';
    if (accounts.length > 0) {
      const ch = accounts.find(a => a.account_type === 'checking');
      const sv = accounts.find(a => a.account_type === 'savings');
      const em = accounts.find(a => a.account_type === 'emergency_fund');
      if (ch && ch.balance > 0) {
        type = 'live';
        newProfile = {
          checking: ch.balance, savings: sv?.balance || 0,
          emergencyFund: em?.balance || 0,
          monthlyIncome: DEFAULT_PROFILE.monthlyIncome,
          monthlyExpenses: DEFAULT_PROFILE.monthlyExpenses,
        };
      }
    }
    setScenarioType(type);
    setProfile(newProfile);
    setScreenRaw('scenario_setup');
    if (!simId && user) {
      const { data } = await supabase.from("homeownership_simulations").insert({
        user_id: user.id, current_screen: 'scenario_setup',
        scenario_type: type === 'live' ? 'live_user_finances' : 'default_profile',
        financial_profile: newProfile as AnyValue,
      }).select().single();
      if (data) setSimId((data).id);
    } else {
      persist('scenario_setup');
    }
  }, [accounts, user, simId, persist]);

  const completePurchase = useCallback(async () => {
    if (!user || !selectedHome || !simId) return;
    setProcessing(true);
    try {
      if (scenarioType === 'live') {
        const { error } = await supabase.rpc("deduct_purchase_funds", { p_amount: cashToClose });
        if (error) throw new Error(error.message);
      }
      await supabase.from("user_assets").insert({
        user_id: user.id, asset_type: 'primary_residence', asset_name: selectedHome.home_name,
        purchase_price: selectedHome.home_price, estimated_value: selectedHome.home_price,
        starting_equity: downPaymentAmount, linked_simulation_id: simId,
      });
      await supabase.from("user_liabilities").insert({
        user_id: user.id, liability_type: 'mortgage', liability_name: `${selectedHome.home_name} Mortgage`,
        original_balance: loanAmount, current_balance: loanAmount, monthly_payment_estimate: monthlyPI,
        linked_simulation_id: simId,
      });
      // Award achievements
      const { data: achs } = await supabase.from("achievements").select("id, name");
      const map = new Map((achs || []).map((a: AnyValue) => [a.name, a.id]));
      const { data: existing } = await supabase.from("user_achievements").select("achievement_id").eq("user_id", user.id);
      const existingIds = new Set((existing || []).map((e: AnyValue) => e.achievement_id));
      const toAward = ['First-Time Buyer', 'Closing Day', 'House Hunter'];
      if (liquidAfter >= 10000) toAward.push('Smart Saver');
      if (affordabilityRating === 'comfortable') toAward.push('Affordable Choice');
      for (const name of toAward) {
        const id = map.get(name);
        if (id && !existingIds.has(id)) {
          await supabase.from("user_achievements").insert({ user_id: user.id, achievement_id: id });
        }
      }
      // Simulated credit purchase on home purchase completion
      try {
        await supabase.rpc("auto_credit_charge", {
          p_amount: 30 + Math.floor(Math.random() * 31),
          p_description: 'Credit Purchase: Home Move-In Essentials & Supplies',
        });
      } catch { /* non-critical: failure is safe to ignore here */ }
      setPurchaseCompleted(true);
      setFinalDecision('proceed');
      // Module cash comes from the server catalogue ($40 capstone tier), once only.
      try {
        await supabase.rpc("record_module_completion" as never, {
          p_module_key: "homeownership", p_score: 1, p_max_score: 1,
        } as never);
        await supabase.rpc("award_module_completion_reward" as never, {
          p_module_key: "homeownership",
        } as never);
      } catch { /* non-critical: failure is safe to ignore here */ }

      // Generate the post-purchase surprise and route to the life-shock screen.
      if (!ref.current.lifeShock.amount) {
        const ev = buildLifeShock(selectedHome);
        setLifeShock(ev);
        ref.current.lifeShock = ev;
      }
      setScreenRaw('life_shock');
      await persist('life_shock', {
        final_decision: 'proceed', purchase_completed: true,
        purchase_completed_at: new Date().toISOString(),
        affordability_rating: affordabilityRating,
      });
      if (scenarioType === 'live') await refetchAccounts();
    } finally {
      setProcessing(false);
    }
  }, [user, selectedHome, simId, scenarioType, cashToClose, downPaymentAmount, loanAmount, monthlyPI, liquidAfter, affordabilityRating, persist, refetchAccounts]);

  const finalizeAfterShock = useCallback(async () => {
    const recap = buildRecap(true);
    setScreenRaw('completed');
    await persist('completed', {
      status: 'completed',
      affordability_rating: affordabilityRating,
      _recap: recap,
    });
  }, [persist, affordabilityRating, buildRecap]);

  const delayPurchase = useCallback(async () => {
    if (!user || !simId) return;
    setProcessing(true);
    try {
      const { data: achs } = await supabase.from("achievements").select("id, name").eq("name", "House Hunter");
      if (achs && achs.length > 0) {
        const achId = (achs)[0].id;
        const { data: ex } = await supabase.from("user_achievements").select("id").eq("user_id", user.id).eq("achievement_id", achId);
        if (!ex || ex.length === 0) {
          await supabase.from("user_achievements").insert({ user_id: user.id, achievement_id: achId });
        }
      }
      recordDecision({
        key: 'final',
        label: 'Final decision',
        choice: 'Delayed the purchase',
        consequence: 'No mortgage, no closing costs, no new monthly bill. Cash position preserved.',
        lumiSignal: 'Saying not yet is free. Saying yes to the wrong house is the most expensive choice on the screen.',
        walletImpact: 0,
        scoreDelta: 10,
      });
      const recap = buildRecap(false);
      setFinalDecision('delay_purchase');
      setScreenRaw('delay_ending');
      await persist('delay_ending', {
        final_decision: 'delay_purchase', status: 'completed',
        affordability_rating: affordabilityRating, _recap: recap,
      });
    } finally {
      setProcessing(false);
    }
  }, [user, simId, affordabilityRating, persist, recordDecision, buildRecap]);

  const resetToHome = useCallback(() => {
    setSelectedHomeKey(null);
    setDownPaymentPct(0.1);
    setMortgageYears(30);
    goTo('home_selection');
  }, [goTo]);

  const resetToDownPayment = useCallback(() => { goTo('down_payment'); }, [goTo]);

  const tryAgain = useCallback(async () => {
    setSelectedHomeKey(null);
    setDownPaymentPct(0.1);
    setMortgageYears(30);
    setFinalDecision(null);
    setPurchaseCompleted(false);
    setDecisionLog([]);
    setCommittedSteps({});
    setLifeShock({ amount: 0, description: '', choice: null });
    setLastDecision(null);
    setScreenRaw('home_selection');
    if (simId) {
      await supabase.from("homeownership_simulations").update({
        current_screen: 'home_selection', status: 'in_progress',
        selected_home_key: null, final_decision: null, purchase_completed: false,
        run_summary: {},
        updated_at: new Date().toISOString(),
      }).eq("id", simId);
    }
  }, [simId]);

  return {
    screen, goTo, loading, processing, homes, profile, selectedHome, selectedHomeKey,
    setSelectedHomeKey, downPaymentPct, setDownPaymentPct, mortgageYears, setMortgageYears,
    downPaymentAmount, closingCosts, loanAmount, rate, monthlyPI, totalMonthlyHousing,
    cashToClose, liquidBefore, liquidAfter, monthlyRemaining, affordabilityRating, canAfford,
    progress, startSim, completePurchase, delayPurchase, resetToHome, resetToDownPayment,
    purchaseCompleted, finalDecision, popupKey, setPopupKey, scenarioType, persist, tryAgain,
    decisionLog, committedSteps, lifeShock, lastDecision,
    commitHomeChoice, commitDownPayment, commitMortgageTerm, commitAffordability,
    ensureLifeShock, commitLifeShock, finalizeAfterShock, buildRecap,
  };
}
